import { useState } from "react";
import { theme, card, emptyFrame, type as text, pageSubtitle } from "../theme";
import { fmtDate } from "../lib/format";
import { fmtDateOnly } from "../lib/dateOnly";
import { contractTiming, CONTRACT_KIND_LABELS, type ContractKind } from "../lib/supplierContracts";
import { downloadCsv } from "../lib/registerExport";
import { PortalLink } from "./PortalLink";
import { KIND_HREFS } from "./EntityTags";
import { secondaryBtn } from "../pages/resilience/shared";

// The cross-supplier working views on the TPRM page (docs/TPRM-CONTRACTS.md):
//   * the renewal and notice calendar — every agreement across suppliers, ordered by the next
//     date that needs a decision (lib/supplierContracts.ts contractTiming, mirrored here);
//   * concentration — which important assets and business impact analyses rest on a supplier,
//     and whether it is a single point of failure (GET /tprm/concentration);
//   * the questionnaire chase — sent and not returned for 14 days (GET /tprm/questionnaires/outstanding).

const DAY = 86_400_000;
const fmt = fmtDate;
const ymd = (d: Date | string | null | undefined) => (d ? new Date(d).toISOString().slice(0, 10) : "");

// ── Renewal and notice calendar ─────────────────────────────────────────────

export interface CalendarContract {
  id: string; title: string; kind: string; status: string; reference: string | null;
  endDate: string | null; autoRenew: boolean; noticePeriodDays: number | null; owner: string | null;
  annualValue: number | null; currency: string | null;
}
export interface CalendarSupplier { id: string; name: string; riskTier: string; internalOwner: string | null; contracts?: CalendarContract[] }

export type CalendarGroup = "overdue" | "d30" | "d90" | "later" | "undated";

export interface CalendarRow {
  supplier: CalendarSupplier;
  contract: CalendarContract;
  group: CalendarGroup;
  /** The date that matters next: the notice deadline before the end date, then the end date. */
  date: Date | null;
  what: string;
  days: number | null;
}

export const CALENDAR_GROUPS: Array<{ key: CalendarGroup; label: string; hint: string }> = [
  { key: "overdue", label: "Overdue", hint: "The notice deadline has passed, or the end date has and the agreement is still recorded as in force." },
  { key: "d30", label: "Next 30 days", hint: "" },
  { key: "d90", label: "31 to 90 days", hint: "" },
  { key: "later", label: "Later", hint: "" },
  { key: "undated", label: "No end date recorded", hint: "Open-ended, or the end date has not been entered." },
];

/** Every agreement still governing a relationship (not draft, expired or terminated), ordered by the next date that matters. */
export function contractCalendar(suppliers: readonly CalendarSupplier[], now = new Date()): CalendarRow[] {
  const rows: CalendarRow[] = [];
  for (const s of suppliers) {
    for (const c of s.contracts ?? []) {
      if (c.status === "draft" || c.status === "terminated" || c.status === "expired") continue;
      const t = contractTiming(c, now);
      const end = c.endDate ? new Date(c.endDate) : null;
      let date: Date | null = null;
      let what = "";
      if (t.noticeDeadline && t.daysToNotice != null && t.daysToNotice >= 0) {
        date = t.noticeDeadline;
        what = c.autoRenew ? "Last day to give notice before it renews" : "Last day to give notice";
      } else if (t.renewalLocked && t.noticeDeadline) {
        // The notice window closed on an auto-renewing agreement: it renews unless it is renegotiated.
        date = t.noticeDeadline;
        what = `Notice deadline passed: it renews automatically${end ? ` on ${fmtDateOnly(end)}` : ""}`;
      } else if (end && t.daysToEnd != null && t.daysToEnd >= 0) {
        // No notice period, or one that has passed on an agreement that simply ends.
        date = end;
        what = t.noticeDeadline ? "Ends (the notice window has closed)" : "Ends";
      } else if (end) {
        date = end;
        what = "Ended: mark it expired or record the renewal";
      }
      const days = date ? Math.round((date.getTime() - now.getTime()) / DAY) : null;
      const group: CalendarGroup = days == null ? "undated" : days < 0 ? "overdue" : days <= 30 ? "d30" : days <= 90 ? "d90" : "later";
      rows.push({ supplier: s, contract: c, group, date, what, days });
    }
  }
  const order = CALENDAR_GROUPS.map((g) => g.key);
  return rows.sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group) || (a.date?.getTime() ?? Infinity) - (b.date?.getTime() ?? Infinity) || a.supplier.name.localeCompare(b.supplier.name));
}

/** Notice deadlines falling in the next `days` days (the TPRM summary tile). */
export function noticeDeadlinesWithin(suppliers: readonly CalendarSupplier[], days: number, now = new Date()): number {
  let n = 0;
  for (const s of suppliers) for (const c of s.contracts ?? []) {
    if (c.status === "draft" || c.status === "terminated" || c.status === "expired") continue;
    const t = contractTiming(c, now);
    if (t.daysToNotice != null && t.daysToNotice >= 0 && t.daysToNotice <= days) n++;
  }
  return n;
}

export function ContractsCalendar({ suppliers, onOpen }: { suppliers: readonly CalendarSupplier[]; onOpen: (supplierId: string) => void }) {
  const [q, setQ] = useState("");
  const all = contractCalendar(suppliers);
  const needle = q.trim().toLowerCase();
  const rows = needle ? all.filter((r) => `${r.supplier.name} ${r.contract.title} ${r.contract.reference ?? ""} ${r.contract.owner ?? ""}`.toLowerCase().includes(needle)) : all;

  function exportCsv() {
    downloadCsv<CalendarRow>("supplier-agreements-calendar", [
      { header: "Group", value: (r) => CALENDAR_GROUPS.find((g) => g.key === r.group)?.label ?? r.group },
      { header: "Date", value: (r) => ymd(r.date) },
      { header: "What", value: (r) => r.what },
      { header: "Supplier", value: (r) => r.supplier.name },
      { header: "Agreement", value: (r) => r.contract.title },
      { header: "Reference", value: (r) => r.contract.reference ?? "" },
      { header: "Kind", value: (r) => CONTRACT_KIND_LABELS[r.contract.kind as ContractKind] ?? r.contract.kind },
      { header: "End date", value: (r) => ymd(r.contract.endDate) },
      { header: "Auto-renews", value: (r) => r.contract.autoRenew },
      { header: "Notice period (days)", value: (r) => r.contract.noticePeriodDays ?? "" },
      { header: "Owner", value: (r) => r.contract.owner ?? r.supplier.internalOwner ?? "" },
      { header: "Annual value", value: (r) => r.contract.annualValue ?? "" },
      { header: "Currency", value: (r) => r.contract.currency ?? "" },
    ], rows);
  }

  return (
    <div>
      <p style={{ ...pageSubtitle, margin: "0 0 12px", maxWidth: 780 }}>
        Every agreement in force across your suppliers, by the next date that needs a decision: the last day to give notice before the end date, then the end date itself. Open a row to review the agreement.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier, agreement, reference or owner" aria-label="Search agreements"
          style={{ padding: "7px 10px", fontSize: text.body.fontSize, borderRadius: theme.radius.sm, border: `1px solid ${theme.color.border}`, background: theme.color.bg, color: theme.color.text, width: 300, maxWidth: "100%", fontFamily: theme.font }} />
        <span style={{ flex: 1 }} />
        <span style={text.hint}>{rows.length === all.length ? `${all.length} shown` : `${rows.length} of ${all.length}`}</span>
        {rows.length > 0 && <button type="button" onClick={exportCsv} style={{ ...secondaryBtn, padding: "7px 12px", fontSize: text.hint.fontSize }}>Export CSV</button>}
      </div>
      {all.length === 0 ? (
        <div style={{ ...emptyFrame, ...text.body, color: theme.color.muted }}>No agreement in force is recorded yet. Add one from a supplier's Contracts tab.</div>
      ) : (
        CALENDAR_GROUPS.map((g) => {
          const list = rows.filter((r) => r.group === g.key);
          if (list.length === 0) return null;
          const danger = g.key === "overdue";
          return (
            <div key={g.key} style={{ ...card, padding: 0, marginBottom: 12, overflow: "hidden", border: `1px solid ${danger ? theme.color.danger : "var(--c-line)"}` }}>
              <div style={{ padding: "10px 14px", background: danger ? theme.color.dangerBg : theme.color.panelAlt, borderBottom: `1px solid ${theme.color.border}` }}>
                <span style={{ ...text.sectionTitle, color: danger ? theme.color.dangerText : theme.color.heading }}>{g.label}</span>
                <span style={{ ...text.hint, marginLeft: 8 }}>{list.length}</span>
                {g.hint && <div style={{ ...text.meta, color: theme.color.subtle, marginTop: 2 }}>{g.hint}</div>}
              </div>
              {list.map((r) => (
                <button key={r.contract.id} type="button" onClick={() => onOpen(r.supplier.id)} title={`Open ${r.supplier.name}: Contracts`} style={{
                  display: "grid", gridTemplateColumns: "minmax(110px, 140px) 1fr auto", gap: 12, width: "100%", textAlign: "left",
                  padding: "10px 14px", border: "none", borderBottom: `1px solid ${theme.color.border}`, background: "transparent", cursor: "pointer", fontFamily: theme.font,
                }}>
                  <div>
                    <div style={{ ...text.itemTitle, color: danger ? theme.color.dangerText : theme.color.heading }}>{r.date ? fmtDateOnly(r.date) : "No date"}</div>
                    {r.days != null && <div style={{ ...text.meta, color: theme.color.subtle }}>{r.days < 0 ? `${-r.days} day${r.days === -1 ? "" : "s"} ago` : r.days === 0 ? "today" : `in ${r.days} day${r.days === 1 ? "" : "s"}`}</div>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...text.itemTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.supplier.name}: {r.contract.title}</div>
                    <div style={{ ...text.hint, color: theme.color.muted }}>
                      {r.what || "In force"}
                      {" · "}{CONTRACT_KIND_LABELS[r.contract.kind as ContractKind] ?? r.contract.kind}
                      {r.contract.endDate ? ` · ends ${fmtDateOnly(r.contract.endDate)}` : ""}
                      {r.contract.noticePeriodDays != null ? ` · ${r.contract.noticePeriodDays} days' notice` : ""}
                      {r.contract.autoRenew ? " · auto-renews" : ""}
                    </div>
                  </div>
                  <div style={{ ...text.meta, color: theme.color.subtle, alignSelf: "center", whiteSpace: "nowrap" }}>{r.contract.owner ?? r.supplier.internalOwner ?? ""}</div>
                </button>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Concentration ────────────────────────────────────────────────────────────

export type SpofVerdict = "yes" | "unknown" | "no";

/** GET /tprm/concentration, one row per supplier (backend lib/supplierConcentration.ts). */
export interface SupplierConcentrationRow {
  supplierId: string;
  name: string;
  status: string;
  riskTier: string;
  assetCount: number;
  importantAssets: Array<{ id: string; name: string; criticality: string; isEssentialFunction: boolean; via: "direct" | "dependency" }>;
  essentialAssets: number;
  bias: Array<{ id: string; name: string; displayId: number | null; basis: "named" | "through_assets" }>;
  singlePointOfFailure: SpofVerdict;
  basis: string;
}

const SPOF_META: Record<SpofVerdict, { label: string; color: string; bg: string }> = {
  yes: { label: "Single point of failure", color: theme.color.dangerText, bg: theme.color.dangerBg },
  unknown: { label: "No alternative recorded", color: theme.color.warningText, bg: theme.color.warningBg },
  no: { label: "Alternative recorded", color: theme.color.successText, bg: theme.color.successBg },
};

export function SpofPill({ verdict }: { verdict: SpofVerdict }) {
  const m = SPOF_META[verdict];
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3, background: m.bg, color: m.color, whiteSpace: "nowrap" }}>{m.label}</span>;
}

const biaLabel = (b: { name: string; displayId: number | null }) => (b.displayId != null ? `BIA-${String(b.displayId).padStart(3, "0")} ${b.name}` : b.name);

/** The card on the TPRM page: suppliers that essential functions rest on, single points of failure first. */
export function ConcentrationCard({ rows, onOpen }: { rows: SupplierConcentrationRow[] | null; onOpen: (supplierId: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  if (!rows) return null;
  const relevant = rows.filter((r) => r.essentialAssets > 0 || r.importantAssets.length > 0 || r.bias.length > 0);
  if (relevant.length === 0) return null;
  const shown = showAll ? relevant : relevant.slice(0, 5);
  const spof = rows.filter((r) => r.singlePointOfFailure === "yes").length;
  const unknown = rows.filter((r) => r.singlePointOfFailure === "unknown").length;
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ ...text.sectionTitle, margin: 0 }}>Concentration</span>
        <span style={text.hint}>
          {spof} single point{spof === 1 ? "" : "s"} of failure{unknown ? `, ${unknown} with no alternative recorded` : ""}
        </span>
      </div>
      <p style={{ ...text.hint, margin: "0 0 10px" }}>
        The important assets that rest on each supplier, directly or through the dependency graph, and the business impact analyses that depend on it. A supplier supporting an essential function is a single point of failure where the DORA register records it as difficult or impossible to substitute; with no answer and no contingency plan it stays flagged.
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        {shown.map((r) => (
          <div key={r.supplierId} style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${theme.color.border}` }}>
            <div style={{ flex: "1 1 280px", minWidth: 0 }}>
              <button type="button" onClick={() => onOpen(r.supplierId)} style={{ padding: 0, border: "none", background: "none", fontFamily: theme.font, ...text.itemTitle, cursor: "pointer", textAlign: "left" }}>{r.name}</button>
              <div style={{ ...text.hint, color: theme.color.muted, marginTop: 2 }}>
                {r.importantAssets.length} important asset{r.importantAssets.length === 1 ? "" : "s"}
                {r.essentialAssets ? ` (${r.essentialAssets} essential)` : ""}
                {r.importantAssets.length ? `: ${r.importantAssets.slice(0, 4).map((a) => `${a.name}${a.via === "dependency" ? " (through dependencies)" : ""}`).join(", ")}${r.importantAssets.length > 4 ? ", …" : ""}` : ""}
              </div>
              {r.bias.length > 0 && <div style={{ ...text.hint, color: theme.color.muted, marginTop: 2 }}>Business impact analyses: {r.bias.map(biaLabel).join(", ")}</div>}
              <div style={{ ...text.meta, color: theme.color.subtle, marginTop: 2 }}>{r.basis}</div>
            </div>
            <SpofPill verdict={r.singlePointOfFailure} />
          </div>
        ))}
      </div>
      {relevant.length > 5 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} style={{ ...secondaryBtn, marginTop: 8, padding: "4px 10px", fontSize: text.hint.fontSize }}>{showAll ? "Show fewer" : `Show all ${relevant.length}`}</button>
      )}
    </div>
  );
}

/** The "Concentration" line in the supplier panel's Overview tab. */
export function ConcentrationLine({ row }: { row: SupplierConcentrationRow | undefined }) {
  if (!row) return null;
  return (
    <div style={{ ...text.hint, marginTop: 16, padding: "10px 12px", borderRadius: 3, background: theme.color.panelAlt, lineHeight: 1.6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ color: theme.color.muted }}>Concentration:</strong>
        {row.assetCount === 0 && row.bias.length === 0
          ? <span>nothing on the asset register or in a business impact analysis rests on this supplier.</span>
          : <span>{row.assetCount} asset{row.assetCount === 1 ? "" : "s"} rest on it ({row.importantAssets.length} important, {row.essentialAssets} essential){row.bias.length ? `; ${row.bias.length} business impact analys${row.bias.length === 1 ? "is depends" : "es depend"} on it` : ""}.</span>}
        {(row.essentialAssets > 0 || row.singlePointOfFailure !== "no") && <SpofPill verdict={row.singlePointOfFailure} />}
      </div>
      {row.importantAssets.length > 0 && (
        <div>
          {row.importantAssets.slice(0, 8).map((a, i) => (
            <span key={a.id}>{i > 0 ? ", " : ""}<PortalLink to={KIND_HREFS.asset(a.id) ?? "/resilience/assets"} style={{ color: theme.color.accentText }}>{a.name}</PortalLink>{a.via === "dependency" ? " (through dependencies)" : ""}</span>
          ))}
          {row.importantAssets.length > 8 ? ` and ${row.importantAssets.length - 8} more` : ""}
        </div>
      )}
      {row.bias.length > 0 && <div>Business impact analyses: {row.bias.map((b) => <span key={b.id}>{biaLabel(b)}{b.basis === "through_assets" ? " (through its assets)" : ""}; </span>)}</div>}
      <div>{row.basis}</div>
    </div>
  );
}

// ── Questionnaire chase ──────────────────────────────────────────────────────

/** GET /tprm/questionnaires/outstanding. */
export interface OutstandingQuestionnaire {
  id: string;
  title: string;
  sentAt: string | null;
  dueDate: string | null;
  daysOutstanding: number;
  pastDueDate: boolean;
  linkExpired: boolean;
  lastRemindedAt: string | null;
  daysSinceReminder: number | null;
  /** Reminders sent so far, by hand or by automatic chasing. */
  reminderCount?: number;
  /** When automatic chasing stopped writing to the supplier and told the organisation's people instead. */
  escalatedAt?: string | null;
  /** The address the questionnaire (or its last reminder) went to. */
  sentTo?: string | null;
  supplier: { id: string; name: string; riskTier: string; hasContactEmail: boolean };
}

/** GET /tprm/questionnaires/outstanding `autoChase`: whether the platform chases by itself. */
export interface AutoChaseSummary { enabled: boolean; intervalDays: number; maxReminders: number }

/**
 * The chase list: a summary per questionnaire. Opening a row opens the questionnaire's view in the
 * supplier panel, where the reminder is sent from its Actions (for people with tprm:write).
 */
export function QuestionnaireChase({ rows, afterDays, autoChase, onOpen }: {
  rows: OutstandingQuestionnaire[] | null;
  afterDays: number;
  autoChase?: AutoChaseSummary | null;
  onOpen: (supplierId: string, questionnaireId: string) => void;
}) {
  if (!rows) return null;

  const chasing = autoChase?.enabled
    ? `Automatic chasing is on: a reminder every ${autoChase.intervalDays} days, at most ${autoChase.maxReminders}, then your team is told.`
    : null;

  if (rows.length === 0) {
    return (
      <div style={{ ...emptyFrame, ...text.body }}>
        No questionnaire has been waiting on a supplier for {afterDays} days or more.
        {chasing && <div style={{ ...text.hint, marginTop: 4 }}>{chasing}</div>}
      </div>
    );
  }
  return (
    <div style={{ ...card }}>
      <div style={{ ...text.sectionTitle, color: theme.color.warningText, marginBottom: 4 }}>
        {rows.length} questionnaire{rows.length === 1 ? "" : "s"} sent and not returned after {afterDays} days
      </div>
      <p style={{ ...text.hint, margin: "0 0 8px" }}>
        {chasing ? `${chasing} ` : ""}Open a questionnaire to send the supplier's contact a reminder with a new link (the old one stops working); the date it was sent stays, so the wait keeps counting.
      </p>
      {rows.map((q) => (
        <button key={q.id} type="button" onClick={() => onOpen(q.supplier.id, q.id)} title={`Open ${q.title}`} style={{
          display: "block", width: "100%", textAlign: "left", padding: "8px 0", border: "none", borderTop: `1px solid ${theme.color.border}`,
          background: "transparent", cursor: "pointer", fontFamily: theme.font,
        }}>
          <div style={text.itemTitle}>{q.supplier.name}</div>
          <div style={{ ...text.hint, color: theme.color.muted }}>
            {q.title} · sent {fmt(q.sentAt)}, {q.daysOutstanding} days ago
            {q.dueDate ? ` · due ${fmtDateOnly(q.dueDate)}${q.pastDueDate ? " (passed)" : ""}` : ""}
            {q.linkExpired ? " · link expired" : ""}
            {q.reminderCount ? ` · ${q.reminderCount} reminder${q.reminderCount === 1 ? "" : "s"} sent` : ""}
            {q.lastRemindedAt ? ` · last reminded ${fmt(q.lastRemindedAt)}` : " · not yet reminded"}
            {q.sentTo ? ` · to ${q.sentTo}` : ""}
          </div>
          {q.escalatedAt && <div style={{ ...text.hint, color: theme.color.dangerText }}>Escalated to your team {fmt(q.escalatedAt)}: no more reminders go to the supplier.</div>}
          {!q.supplier.hasContactEmail && <div style={{ ...text.hint, color: theme.color.warningText }}>No contact email on the supplier: add one to send a reminder.</div>}
        </button>
      ))}
    </div>
  );
}
